import { useState, useEffect } from 'react';
import { PILLARS, getScoreColor, calculateNextIndex } from '../utils/scoring';
import { getBenchmarks, submitBenchmark } from '../utils/benchmarks';

export default function BenchmarkCompare({ audit }) {
  const [benchmarks, setBenchmarks] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      // Submit this audit to benchmarks
      await submitBenchmark(audit);
      // Get averages
      const data = await getBenchmarks(audit.intake?.city, null);
      setBenchmarks(data);
      setLoading(false);
    }
    load();
  }, [audit.id]);

  const scores = audit.scores || {};
  const nextIndex = calculateNextIndex(scores);

  if (loading) return (
    <div className="card p-6">
      <h3 className="font-display text-lg text-white mb-2">Industry Benchmarks</h3>
      <p className="text-xs text-neutral-500 animate-pulse">Loading benchmark data...</p>
    </div>
  );

  if (!benchmarks || benchmarks.total < 2) return (
    <div className="card p-6">
      <h3 className="font-display text-lg text-white mb-2">Industry Benchmarks</h3>
      <p className="text-xs text-neutral-500">Not enough data yet. Benchmarks require at least 3 audits. You have {benchmarks?.total || 0}.</p>
      <p className="text-xs text-neutral-600 mt-1">Keep auditing — averages will appear as your database grows.</p>
    </div>
  );

  const allAvg = benchmarks.all;
  const marketAvg = benchmarks.market;

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-display text-lg text-white">Industry Benchmarks</h3>
          <p className="text-xs text-neutral-500">
            Based on {benchmarks.total} audit{benchmarks.total !== 1 ? 's' : ''}
            {marketAvg && ` (${benchmarks.marketCount} in your market)`}
          </p>
        </div>
      </div>

      {/* Overall comparison */}
      <div className="grid grid-cols-3 gap-3 mb-5">
        <div className="bg-neutral-800/40 rounded-lg p-3 text-center">
          <p className="text-xs text-neutral-500 mb-1">This Property</p>
          <p className="text-2xl font-black text-brand-gold">{nextIndex.toFixed(1)}</p>
        </div>
        <div className="bg-neutral-800/40 rounded-lg p-3 text-center">
          <p className="text-xs text-neutral-500 mb-1">All Audits Avg</p>
          <p className="text-2xl font-black text-neutral-400">{allAvg?.avgIndex?.toFixed(1) || '—'}</p>
        </div>
        {marketAvg && (
          <div className="bg-neutral-800/40 rounded-lg p-3 text-center">
            <p className="text-xs text-neutral-500 mb-1">Market Avg</p>
            <p className="text-2xl font-black text-neutral-400">{marketAvg.avgIndex.toFixed(1)}</p>
          </div>
        )}
      </div>

      {/* Pillar comparison */}
      <div className="space-y-2">
        {PILLARS.map(p => {
          const yours = scores[p.id]?.score || 0;
          const allPillarAvg = allAvg?.avgScores?.[p.id] || 0;
          const marketPillarAvg = marketAvg?.avgScores?.[p.id] || 0;
          const delta = yours - allPillarAvg;
          const yourColor = getScoreColor(yours);

          return (
            <div key={p.id} className="flex items-center gap-3 text-xs">
              <span className="text-neutral-400 w-32 truncate">{p.name}</span>
              <span className={`font-bold w-8 text-right ${yourColor.text}`}>{yours > 0 ? yours.toFixed(1) : '—'}</span>
              <div className="flex-1 h-2 bg-neutral-800 rounded-full relative overflow-hidden">
                {/* All avg marker */}
                {allPillarAvg > 0 && (
                  <div className="absolute top-0 h-full w-0.5 bg-neutral-500 z-10" style={{ left: `${(allPillarAvg / 10) * 100}%` }} title={`Avg: ${allPillarAvg}`} />
                )}
                {/* Your bar */}
                <div className="h-full rounded-full" style={{ width: `${(yours / 10) * 100}%`, backgroundColor: yourColor.hex }} />
              </div>
              <span className={`w-10 text-right font-bold ${delta > 0 ? 'text-emerald-400' : delta < 0 ? 'text-red-400' : 'text-neutral-500'}`}>
                {yours > 0 && allPillarAvg > 0 ? `${delta > 0 ? '+' : ''}${delta.toFixed(1)}` : ''}
              </span>
            </div>
          );
        })}
      </div>

      <div className="flex items-center gap-3 mt-3 text-[10px] text-neutral-600">
        <span>— Your Score</span>
        <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-neutral-500" /> All Audits Avg</span>
      </div>
    </div>
  );
}
